import { storageGet, storageSet } from './storage';
import { apiFetch } from '../hooks/useApi';

/**
 * Billing store — per-user browser mirrors of SERVER data, plus one genuinely
 * local convenience (bookmarked line items).
 *
 * Nothing here is seeded with sample data. Company data — clients, the rate card,
 * numbering, invoice defaults, email templates, client discounts — is canonical on
 * the server (/api/contractor/clients and the contractor_settings blob). These
 * mirrors exist only so synchronous readers (the PDF builder, Settings previews)
 * have the last copy the server sent. Document numbers are always assigned by the
 * server; there is no browser counter.
 *
 * Keys are namespaced per signed-in user via utils/storage.js, so two people on a
 * shared device never see each other's cache, and logout clears them.
 *
 * Saved line items are company-shared on the server (see loadSavedLines); the
 * browser copy only holds lines that couldn't reach the server yet.
 */

const CLIENTS_KEY = 'billing_clients';           // mirror of /api/contractor/clients
const PRESETS_KEY = 'billing_item_bookmarks';    // lines waiting to move to the server's shared list
const RATES_KEY = 'billing_rates';
const INVOICE_DEFAULTS_KEY = 'invoice_defaults';
const CLIENT_RATES_KEY = 'client_rates';
const NUMBERING_KEY = 'doc_numbering';
const EMAIL_TPL_KEY = 'email_templates';

const uid = (p) => `${p}-${Math.random().toString(36).slice(2, 9)}`;
const readArr = (key) => { const v = storageGet(key, []); return Array.isArray(v) ? v : []; };
const readObj = (key) => { const v = storageGet(key, {}); return v && typeof v === 'object' && !Array.isArray(v) ? v : {}; };

/* ── Clients (mirror of the real Clients module) ─────────────────────── */

/** Map a /api/contractor/clients row to the small shape billing uses. */
export const toBillingClient = (c) => ({
  id: c.id,
  name: c.name || '',
  contact: c.contact_name || '',
  email: c.contact_email || '',
  phone: c.contact_phone || '',
  address: [c.address, c.postcode].filter(Boolean).join('\n'),
  status: c.status || 'active',
});

/** Last client list the server returned (empty until Billing or Clients has loaded it). */
export function getClients() { return readArr(CLIENTS_KEY); }

/** Replace the mirror with a fresh server list (rows from /api/contractor/clients). */
export function cacheClients(rows) {
  const list = (Array.isArray(rows) ? rows : []).map((c) => (c && c.contact_name !== undefined ? toBillingClient(c) : c)).filter((c) => c && c.name);
  storageSet(CLIENTS_KEY, list);
  return list;
}

export function getClientByName(name) {
  const k = String(name || '').trim().toLowerCase();
  if (!k) return null;
  return getClients().find((c) => c.name.toLowerCase() === k) || null;
}

/* ── Rate card + invoice defaults (mirrors of contractor_settings) ────── */

const DEFAULT_INVOICE_DEFAULTS = {
  notes: '',
  defaultLines: [],
  // Quote validity. 60 days is a common UK default; editable in Settings.
  quoteValidDays: 60,
  // Generic terms printed on the back of a quote — editable in Settings so each
  // firm sets its own wording. Not sample data: these are neutral defaults.
  quoteTerms: [
    'The above items are subject to final design and pricing that might require minor modifications.',
    'Works quoted are to be carried out during normal working hours, unless otherwise agreed (Mon–Fri, 08:00–17:00).',
    'Prior to works commencing we will provide RAMS and all associated H&S documentation if required or requested.',
    'Access to the work areas via communal access routes, specific equipment or access requirements must be made available and arranged by the client prior to the works commencing.',
    'Should we, or a sub-contractor employed by us to complete the quoted works, attend site and be unable to carry out the works through no fault of our own, charges will be made for that day’s labour, any materials used and any other costs incurred.',
    'A demobilisation charge will be applicable for cancellation with less than 48 hours’ notice.',
  ].join('\n'),
  // Section presets offered when writing a quotation body; `kind: 'table'` gives a
  // costed breakdown instead of prose. Editable in Settings.
  quoteSectionPresets: [
    { title: 'Scope of Works', body: 'We are pleased to provide our quotation to undertake…', bullets: [] },
    { title: 'Programme', body: 'We anticipate that the works will require approximately … working days, subject to weather conditions and uninterrupted access.', bullets: [] },
    { title: 'Site Requirements', body: 'All necessary access arrangements, permits and site inductions are to be provided by the client/site management team.', bullets: [] },
    { title: 'Access / Specialist Access', body: 'This quotation is based on all areas being accessible from the existing equipment. Any area requiring specialist access will be identified and quoted separately.', bullets: [] },
    { title: 'Deliverables', body: 'Upon completion we will provide a written report detailing:', bullets: ['Areas inspected.', 'Defects and areas of concern identified.', 'Photographic evidence where applicable.', 'Recommendations for further investigation or remedial works.'] },
    { title: 'Exclusions', body: 'Unless specifically stated above, the following are excluded:', bullets: [] },
    { title: 'Commercial Basis', body: 'Any planned days cancelled with less than 24 hours’ notice will be subject to the full daily charge.', bullets: [] },
    { title: 'Budget Summary', kind: 'table', body: 'Based on the current information available, the maximum preliminary budget is:', rows: [{ label: '', amount: '' }] },
  ],
  quoteAcceptance: 'The above items have been priced to be completed as one instruction; should the client require to instruct part of the quote, the price(s) of the single items may be revised.\nIf the above quote meets your requirements, please provide a purchase order stating the quote value and reference number to confirm you are willing to proceed.\nPlease contact us should you have any queries.',
};

/** The company rate card (empty until the server settings have loaded). */
export function getBillingRates() { return readArr(RATES_KEY); }
export function saveBillingRates(list) { const arr = Array.isArray(list) ? list : []; storageSet(RATES_KEY, arr); return arr; }
export const rateToPreset = (r) => ({ id: r.id, description: r.label, unit: r.unit || 'each', unit_cost: Number(r.cost) || 0, unit_sale: Number(r.price) || 0 });

export function getInvoiceDefaults() { return { ...DEFAULT_INVOICE_DEFAULTS, ...readObj(INVOICE_DEFAULTS_KEY) }; }
/** Mirror only — merges onto the CURRENT values, so saving one key never wipes the others. */
export function saveInvoiceDefaults(obj) {
  const v = { ...getInvoiceDefaults(), ...(obj || {}) };
  storageSet(INVOICE_DEFAULTS_KEY, v);
  return v;
}

/** Refresh every mirror from the server settings blob (GET /api/contractor/settings → data). */
export function hydrateBillingSettings(data) {
  if (!data || typeof data !== 'object') return;
  if (Array.isArray(data.billingRates)) storageSet(RATES_KEY, data.billingRates);
  if (data.invoiceDefaults && typeof data.invoiceDefaults === 'object') storageSet(INVOICE_DEFAULTS_KEY, { ...DEFAULT_INVOICE_DEFAULTS, ...data.invoiceDefaults });
  if (Array.isArray(data.clientRates)) storageSet(CLIENT_RATES_KEY, data.clientRates);
  if (data.docNumbering && typeof data.docNumbering === 'object') storageSet(NUMBERING_KEY, data.docNumbering);
  if (data.emailTemplates && typeof data.emailTemplates === 'object') storageSet(EMAIL_TPL_KEY, data.emailTemplates);
}

/* ── Email templates (Settings → Email templates) ─────────────────────── */
// Placeholders like {client} {doc_no} {type} {property} {job} {company} are substituted.
export function getEmailTemplates() { return readObj(EMAIL_TPL_KEY); }
export function saveEmailTemplates(obj) { const v = obj && typeof obj === 'object' ? obj : {}; storageSet(EMAIL_TPL_KEY, v); return v; }
export function fillTemplate(str, vars = {}) { return String(str || '').replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null && vars[k] !== '' ? String(vars[k]) : `{${k}}`)); }

/* ── Client-specific discounts (Settings → Client rates) ──────────────── */
export function getClientRates() { return readArr(CLIENT_RATES_KEY); }
export function saveClientRates(list) { const arr = Array.isArray(list) ? list : []; storageSet(CLIENT_RATES_KEY, arr); return arr; }
export function clientRateFor(name) {
  if (!name) return null;
  const k = String(name).trim().toLowerCase();
  return getClientRates().find((c) => String(c.client || '').trim().toLowerCase() === k) || null;
}

/* ── Line-item presets = company rate card ∪ the company's shared saved lines ──
   Saved lines used to live only in this browser, so a colleague never saw them.
   They now live on the server (GET/POST /api/contractor/saved-lines);
   loadSavedLines() moves any browser-saved lines across once, then clears them. */
let serverLines = [];

export async function loadSavedLines() {
  const local = readArr(PRESETS_KEY);
  if (local.length) {
    try {
      const r = await apiFetch('/api/contractor/saved-lines/import', { method: 'POST', body: JSON.stringify({ lines: local }) });
      serverLines = Array.isArray(r?.lines) ? r.lines : serverLines;
      storageSet(PRESETS_KEY, []);
      return getItemPresets();
    } catch { /* keep the local copy and try again next time */ }
  }
  try {
    const r = await apiFetch('/api/contractor/saved-lines');
    serverLines = Array.isArray(r?.lines) ? r.lines : [];
  } catch { /* offline — rates + any local lines still work */ }
  return getItemPresets();
}

export function getItemPresets() {
  const rates = getBillingRates().map(rateToPreset);
  const shared = serverLines.map((l) => ({
    id: `s-${l.id}`, description: l.description, unit: l.unit || 'each', kind: l.kind || undefined,
    unit_cost: Number(l.unit_cost) || 0, unit_sale: Number(l.unit_sale) || 0,
  }));
  const adhoc = readArr(PRESETS_KEY);        // only until loadSavedLines() has moved them
  const byDesc = new Map();
  [...rates, ...shared, ...adhoc].forEach((p) => { const k = String(p.description || '').toLowerCase(); if (k && !byDesc.has(k)) byDesc.set(k, p); });
  return Array.from(byDesc.values());
}

/**
 * Save a line to the company's shared saved items (never touches the admin rate
 * card, which is Settings → Billing rates). Returns { presets, saved, reason }:
 * saved=false with reason 'rate' when the line is already a company rate. If the
 * server can't be reached the line is kept in this browser and moved up later.
 */
export function saveItemPreset(preset) {
  const description = String(preset.description || '').trim();
  if (!description) return { presets: getItemPresets(), saved: false };
  const rateDescs = new Set(getBillingRates().map((r) => String(r.label || '').toLowerCase()));
  if (rateDescs.has(description.toLowerCase())) return { presets: getItemPresets(), saved: false, reason: 'rate' };
  const record = { description, unit_cost: Number(preset.unit_cost) || 0, unit_sale: Number(preset.unit_sale) || 0, kind: preset.kind || 'labour' };
  const idx = serverLines.findIndex((l) => String(l.description).toLowerCase() === description.toLowerCase());
  const optimistic = { id: idx >= 0 ? serverLines[idx].id : uid('tmp'), ...record };
  serverLines = idx >= 0 ? serverLines.map((l, i) => (i === idx ? optimistic : l)) : [...serverLines, optimistic];
  apiFetch('/api/contractor/saved-lines', { method: 'POST', body: JSON.stringify(record) })
    .then((r) => { if (r?.line) serverLines = serverLines.map((l) => (l.description === description ? r.line : l)); })
    .catch(() => {
      const adhoc = readArr(PRESETS_KEY);
      if (!adhoc.some((p) => String(p.description).toLowerCase() === description.toLowerCase())) storageSet(PRESETS_KEY, [...adhoc, { id: uid('p'), ...record }]);
    });
  return { presets: getItemPresets(), saved: true };
}

/* ── Document numbering (Settings → Numbering) ────────────────────────
   Prefix + zero-padding per type, and an optional floor for the running count.
   The SERVER allocates every number (GET /billing/next-number shows the next one);
   this mirror only drives previews and the format check on a hand-typed number. */
const DEFAULT_NUMBERING = {
  Quote: { prefix: 'QUO', pad: 4, count: 0 },
  Invoice: { prefix: 'INV', pad: 4, count: 0 },
  PO: { prefix: 'PO', pad: 4, count: 0 },
  'Sales Order': { prefix: 'SO', pad: 4, count: 0 },
  'Credit Note': { prefix: 'CN', pad: 4, count: 0 },
};
export const DOC_TYPES = Object.keys(DEFAULT_NUMBERING);

function readNumbering() {
  const saved = readObj(NUMBERING_KEY);
  const out = {};
  for (const t of DOC_TYPES) {
    const d = DEFAULT_NUMBERING[t];
    const s = saved[t] || {};
    out[t] = {
      prefix: s.prefix !== undefined && s.prefix !== null ? String(s.prefix) : d.prefix,
      pad: Number.isFinite(Number(s.pad)) && s.pad !== '' ? Math.max(0, Math.floor(Number(s.pad))) : d.pad,
      count: Number.isFinite(Number(s.count)) && s.count !== '' ? Math.max(0, Math.floor(Number(s.count))) : d.count,
    };
  }
  return out;
}
const cfgFor = (type) => readNumbering()[type] || { prefix: '', pad: 0, count: 0 };

/** Full per-type numbering config { prefix, pad, count }. */
export function getNumbering() { return readNumbering(); }
/** Persist the numbering mirror (Settings → Numbering saves the server copy itself). */
export function saveNumbering(cfg) {
  const clean = {};
  for (const t of DOC_TYPES) {
    const c = (cfg && cfg[t]) || {};
    clean[t] = {
      prefix: String(c.prefix ?? DEFAULT_NUMBERING[t].prefix).trim().slice(0, 12),
      pad: Math.max(0, Math.min(10, Math.floor(Number(c.pad)) || 0)),
      count: Math.max(0, Math.floor(Number(c.count)) || 0),
    };
  }
  storageSet(NUMBERING_KEY, clean);
  return clean;
}

/** Format a count as a document number for previews ("INV-0012"). */
export function formatDocNo(type, count) {
  const c = cfgFor(type);
  return `${c.prefix ? `${c.prefix}-` : ''}${String(count).padStart(c.pad || 0, '0')}`;
}

/** Does a hand-typed number follow the configured format for its type? */
export function docNoMatchesRules(type, docNo) {
  const s = String(docNo || '').trim();
  const c = cfgFor(type);
  if (!s) return { ok: false, reason: 'a number is required' };
  const pre = c.prefix ? `${c.prefix}-` : '';
  if (pre && !s.startsWith(pre)) return { ok: false, reason: `it must start with “${pre}”` };
  const numPart = pre ? s.slice(pre.length) : s;
  if (!/^\d+$/.test(numPart)) return { ok: false, reason: 'the part after the prefix must be digits' };
  if (c.pad && numPart.length < c.pad) return { ok: false, reason: `pad it to ${c.pad} digits` };
  return { ok: true };
}
